// The home page's "this week" block (GET /api/highlights): what runs now and
// next (War of Emperium and the events on the admin Schedule page), who holds
// the castles, the reigning King of Emperium (once a window has had a winner),
// and the top five players and guilds. Times are the viewer's own.
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { CalendarEntry, GuildRankingRow, HighlightsResponse, KoeKing, RankingRow } from '@shared/types';
import { api } from '@/lib/api';
import { count, dateTime, weekdayTime } from '@/lib/format';
import { Countdown, Emblem, JobIcon } from '@/components/bits';
import { IconArrowRight, IconCastle, IconCrown, IconShield, IconSword, IconTrophy } from '@/components/icons';
import { Badge, cx, Empty, LoadError, Loading, Panel, SectionTitle } from '@/components/ui';

const timeOnly = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });

function MoreLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.12em] text-gold hover:text-gold-bright">
      {children} <IconArrowRight size={16} />
    </Link>
  );
}

function KindBadge({ e }: { e: CalendarEntry }) {
  if (e.live) return <Badge tone="danger">Live</Badge>;
  return e.kind === 'woe' ? <Badge tone="gold">WoE</Badge> : <Badge tone="info">Event</Badge>;
}

/** "Sat 8:00 PM to 10:00 PM", "Sat 8:00 PM", "until 10:00 PM", "running now". */
function when(e: CalendarEntry): string {
  if (e.live) return e.endsAt ? `until ${timeOnly.format(new Date(e.endsAt))}` : 'running now';
  const start = weekdayTime(e.startsAt!);
  return e.endsAt ? `${start} to ${timeOnly.format(new Date(e.endsAt))}` : start;
}

function ComingUp({ entries, className }: { entries: CalendarEntry[]; className?: string }) {
  // The big slot: a siege running now, else whatever starts next (with its
  // countdown); an event that is simply switched on stays in the list.
  const first = entries.find((e) => e.live && e.kind === 'woe') ?? entries.find((e) => !e.live) ?? entries[0];
  const rest = entries.filter((e) => e !== first);
  return (
    <Panel inner="p-6 h-full" tone={first?.live ? 'gold' : 'panel'} className={className}>
      <p className="eyebrow">&#9670; Coming up</p>
      {!first ? (
        <p className="mt-4 text-muted">Nothing on the calendar this week. Check the news for the next event.</p>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
          <div>
            {first.live ? <Badge tone="danger">Live now</Badge> : <p className="text-sm font-semibold uppercase tracking-[0.14em] text-dim">Next up</p>}
            <p className="mt-2 font-display text-2xl leading-tight tracking-wide text-ink">{first.name}</p>
            {first.live ? (
              <p className="mt-2 text-muted">
                {first.endsAt ? (
                  <>
                    Ends in <Countdown to={first.endsAt} className="font-semibold text-ink" />
                  </>
                ) : (
                  'Running now. Join in the game.'
                )}
              </p>
            ) : (
              <>
                <p className="mt-1 text-muted">{when(first)}</p>
                <p className="mt-3 text-3xl font-semibold text-gold-bright">
                  <Countdown to={first.startsAt!} />
                </p>
              </>
            )}
          </div>
          {rest.length ? (
            <ul className="space-y-2 text-sm">
              {rest.map((e) => (
                <li key={`${e.kind}-${e.name}-${e.startsAt ?? 'live'}`} className="flex items-center gap-3 border-b border-line/50 pb-2 last:border-0">
                  <KindBadge e={e} />
                  <div className="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between sm:gap-3">
                    <p className="truncate text-ink">{e.name}</p>
                    <p className="text-xs text-muted sm:shrink-0 sm:text-right sm:text-sm">{when(e)}</p>
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
      <p className="mt-4 text-xs text-dim">Times are in your own time zone.</p>
    </Panel>
  );
}

function CastleHolders({ castles }: { castles: HighlightsResponse['castles'] }) {
  const shown = castles.holders.slice(0, 5);
  const more = castles.holders.length - shown.length;
  return (
    <Panel inner="p-6 h-full flex flex-col">
      <p className="eyebrow">&#9670; Castle holders</p>
      <p className="mt-3">
        <span className="num font-display text-3xl text-gold-bright">{castles.held}</span>
        <span className="text-muted"> of {castles.total} castles held</span>
      </p>
      {shown.length ? (
        <ul className="mt-4 space-y-3">
          {shown.map((g) => (
            <li key={g.id} className="flex items-start gap-3">
              <Emblem url={g.emblemUrl} />
              <div className="min-w-0 flex-1">
                <Link to={`/guilds/${g.id}`} className="block truncate font-semibold text-ink hover:text-gold-bright">
                  {g.name}
                </Link>
                <p className="truncate text-xs text-dim" title={g.castles.join(', ')}>
                  {g.castles.join(', ')}
                </p>
              </div>
              <span className="num flex shrink-0 items-center gap-1 text-sm text-gold-bright">
                <IconCastle size={15} />
                {g.castles.length}
              </span>
            </li>
          ))}
          {more > 0 ? <li className="text-sm text-dim">and {count(more, 'more guild')}</li> : null}
        </ul>
      ) : (
        <p className="mt-4 text-muted">No guild holds a castle yet. The next siege decides who does.</p>
      )}
      <div className="mt-auto">
        <MoreLink to="/woe">Every castle</MoreLink>
      </div>
    </Panel>
  );
}

/** 754 -> "12:34": a hold in minutes and seconds, as the game prints it. */
const holdTime = (seconds: number) => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;

/**
 * The guild that owned the Emperium when the last King of Emperium window
 * ended, with that window's First Blood and Iron Guard under it. Shown only
 * once there is one.
 */
function KingOfEmperium({ king }: { king: KoeKing }) {
  const g = king.guild;
  const name = 'truncate font-display text-2xl leading-tight tracking-wide text-ink';
  return (
    <Panel inner="p-6" tone="gold">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between md:gap-8">
        <div className="min-w-0">
          <p className="eyebrow flex items-center gap-2">
            <IconCrown size={16} /> King of Emperium
          </p>
          <div className="mt-3 flex items-center gap-3">
            {g.emblemUrl ? <Emblem url={g.emblemUrl} size={32} /> : null}
            <div className="min-w-0">
              {g.id !== null ? (
                <Link to={`/guilds/${g.id}`} className={cx(name, 'block hover:text-gold-bright')}>
                  {g.name}
                </Link>
              ) : (
                <p className={name}>{g.name}</p>
              )}
              <p className="text-sm text-muted">Won {dateTime(king.wonAt)}</p>
            </div>
          </div>
        </div>
        {king.firstBlood || king.ironGuard ? (
          <ul className="min-w-0 space-y-2 text-sm md:shrink-0">
            {king.firstBlood ? (
              <li className="flex items-center gap-2">
                <IconSword size={15} className="shrink-0 text-gold" />
                <span className="shrink-0 text-dim">First Blood</span>
                <span className="min-w-0 truncate text-ink">
                  {king.firstBlood.player}
                  {king.firstBlood.guild ? <span className="text-muted"> of {king.firstBlood.guild}</span> : null}
                </span>
              </li>
            ) : null}
            {king.ironGuard ? (
              <li className="flex items-center gap-2">
                <IconShield size={15} className="shrink-0 text-gold" />
                <span className="shrink-0 text-dim">Iron Guard</span>
                <span className="min-w-0 truncate text-ink">{king.ironGuard.guild}</span>
                {/* Outside the truncating name: a long guild name must not cut the time off. */}
                <span className="num shrink-0 text-gold-bright">{holdTime(king.ironGuard.seconds)}</span>
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>
    </Panel>
  );
}

function Rank({ n }: { n: number }) {
  return <span className={cx('num w-6 shrink-0 text-center font-display text-lg', n === 1 ? 'text-gold-bright' : 'text-dim')}>{n}</span>;
}

function TopPlayers({ rows }: { rows: RankingRow[] }) {
  return (
    <Panel inner="p-6 h-full">
      <p className="eyebrow flex items-center gap-2">
        <IconTrophy size={16} /> Top players
      </p>
      {rows.length ? (
        <ol className="mt-4 space-y-2.5">
          {rows.map((r) => (
            <li key={r.name} className="flex items-center gap-3">
              <Rank n={r.rank} />
              <JobIcon id={r.jobId} size={28} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-ink">{r.name}</p>
                <p className="truncate text-xs text-dim">
                  {r.job}
                  {r.guild ? ` · ${r.guild}` : ''}
                </p>
              </div>
              <span className="num shrink-0 text-sm text-gold-bright">
                Lv {r.baseLevel}
                <span className="text-dim">/{r.jobLevel}</span>
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="mt-4 text-muted">No one is ranked yet.</p>
      )}
      <MoreLink to="/rankings">Full rankings</MoreLink>
    </Panel>
  );
}

function TopGuilds({ rows }: { rows: GuildRankingRow[] }) {
  return (
    <Panel inner="p-6 h-full">
      <p className="eyebrow flex items-center gap-2">
        <IconCrown size={16} /> Top guilds
      </p>
      {rows.length ? (
        <ol className="mt-4 space-y-2.5">
          {rows.map((g) => (
            <li key={g.id} className="flex items-center gap-3">
              <Rank n={g.rank} />
              <Emblem url={g.emblemUrl} size={28} />
              <div className="min-w-0 flex-1">
                <Link to={`/guilds/${g.id}`} className="block truncate font-semibold text-ink hover:text-gold-bright">
                  {g.name}
                </Link>
                <p className="truncate text-xs text-dim">
                  Lv {g.level} · {count(g.members, 'member')}
                </p>
              </div>
              {g.castles ? (
                <span className="num flex shrink-0 items-center gap-1 text-sm text-gold-bright" title={count(g.castles, 'castle')}>
                  <IconCastle size={15} />
                  {g.castles}
                </span>
              ) : null}
            </li>
          ))}
        </ol>
      ) : (
        <p className="mt-4 text-muted">No guilds yet.</p>
      )}
      <MoreLink to="/rankings?by=guild">Guild ranking</MoreLink>
    </Panel>
  );
}

export function WorldNow() {
  const q = useQuery({ queryKey: ['highlights'], queryFn: () => api<HighlightsResponse>('/highlights'), refetchInterval: 60_000 });
  return (
    <section className="pt-16 sm:pt-20">
      <SectionTitle eyebrow="Live from the world" title="This week in the world" />
      {q.isError ? (
        <Panel>
          <LoadError what="this week's events" error={q.error} retry={() => q.refetch()} />
        </Panel>
      ) : q.isPending ? (
        <Loading />
      ) : !q.data ? (
        <Panel>
          <Empty title="Nothing to show yet" />
        </Panel>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <ComingUp entries={q.data.calendar} className="lg:col-span-2" />
            <CastleHolders castles={q.data.castles} />
          </div>
          {q.data.king ? <KingOfEmperium king={q.data.king} /> : null}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TopPlayers rows={q.data.topPlayers} />
            <TopGuilds rows={q.data.topGuilds} />
          </div>
        </div>
      )}
    </section>
  );
}
